'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { sr } from './sr';
import { LANG_COOKIE, type Lang } from './config';

export { LANG_COOKIE, type Lang } from './config';

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** Translate an English UI string. English text is the key; unknown keys fall back to English. */
  t: (text: string) => string;
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: 'sr',
  setLang: () => {},
  t: (text) => text,
});

export function LanguageProvider({
  initialLang,
  children,
}: {
  initialLang: Lang;
  children: React.ReactNode;
}) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    // Cookie (not localStorage) so the server can render the right language on first paint.
    document.cookie = `${LANG_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`;
    document.documentElement.lang = next === 'sr' ? 'sr-Latn' : 'en';
  }, []);

  const t = useCallback(
    (text: string) => (lang === 'sr' ? (sr[text] ?? text) : text),
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  return useContext(LanguageContext);
}
